import React, { useState, useEffect } from 'react';
import { X, Search, Car, Phone, Star, MapPin, Check, User } from 'lucide-react';
import { getDrivers } from '../../services/api';
import { getRideDestination } from '../../utils/rideFormat';

const AssignDriverModal = ({ isOpen, onClose, ride, onAssign, queueDrivers = [] }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
    }
  }, [isOpen]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const data = await getDrivers();
      // Filter only online drivers
      const onlineDrivers = data.filter(d => d.status === 'online');
      setDrivers(onlineDrivers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const query = searchQuery.toLowerCase();
    return (
      driver.name?.toLowerCase().includes(query) ||
      driver.phone?.includes(query) ||
      driver.vehicle?.plate?.toLowerCase().includes(query)
    );
  });

  // Sort: queue drivers first, then by name
  const sortedDrivers = [...filteredDrivers].sort((a, b) => {
    const aInQueue = queueDrivers.some(q => q.driver_id === a.id);
    const bInQueue = queueDrivers.some(q => q.driver_id === b.id);
    
    if (aInQueue && !bInQueue) return -1;
    if (!aInQueue && bInQueue) return 1;
    
    // If both in queue, sort by position
    if (aInQueue && bInQueue) {
      const aPos = queueDrivers.find(q => q.driver_id === a.id)?.queue_position || 999;
      const bPos = queueDrivers.find(q => q.driver_id === b.id)?.queue_position || 999;
      return aPos - bPos;
    }
    
    return (a.name || '').localeCompare(b.name || '');
  });

  const handleAssign = async () => {
    if (!selectedDriver) return;
    
    setAssigning(true);
    try {
      await onAssign(ride.id, selectedDriver.id);
      onClose();
    } catch (error) {
      console.error('Error assigning driver:', error);
    } finally {
      setAssigning(false);
    }
  };

  const getQueuePosition = (driverId) => {
    const queueEntry = queueDrivers.find(q => q.driver_id === driverId);
    return queueEntry?.queue_position;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Cakto Shofer
            </h2>
            {ride && (
              <p className="text-sm text-gray-500 mt-1">
                {ride.ticket_number} • {getRideDestination(ride)}
              </p>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Kërko sipas emrit, targës, ose telefonit..."
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="driver-search-input"
            />
          </div>
        </div>

        {/* Driver List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              Duke ngarkuar shoferët...
            </div>
          ) : sortedDrivers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Car className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nuk ka shoferë online</p>
            </div>
          ) : (
            <div className="divide-y">
              {sortedDrivers.map((driver) => {
                const queuePos = getQueuePosition(driver.id);
                const isSelected = selectedDriver?.id === driver.id;
                
                return (
                  <button
                    key={driver.id}
                    onClick={() => setSelectedDriver(driver)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition flex items-center gap-4 ${
                      isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    data-testid={`driver-option-${driver.id}`}
                  >
                    {/* Avatar / Queue Position */}
                    <div className="relative">
                      {queuePos ? (
                        <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg">
                          #{queuePos}
                        </div>
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold"
                          style={{ background: 'linear-gradient(135deg, #fabc0c, #e0a80a)', color: '#1b1c1b' }}
                        >
                          {driver.name?.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      {/* Online indicator */}
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                    </div>

                    {/* Driver Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 truncate">
                          {driver.name}
                        </span>
                        {queuePos && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                            Në Radhë
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Car className="w-3.5 h-3.5" />
                          {driver.vehicle?.plate || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {driver.phone}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-400" />
                          {driver.rating || 0}
                        </span>
                        <span>{driver.total_rides || 0} udhëtime</span>
                      </div>
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100"
            >
              Anulo
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedDriver || assigning}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Cakto Shoferin
                </>
              )}
            </button>
          </div>
          {selectedDriver && (
            <p className="text-center text-sm text-gray-500 mt-2">
              Do të caktohet: <span className="font-semibold">{selectedDriver.name}</span> ({selectedDriver.vehicle?.plate})
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignDriverModal;
